'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@achra/ui/button';
import { Checkbox } from '@achra/ui/checkbox';
import { Dialog, DialogDescription, DialogHeader, DialogOverlay, DialogPortal, DialogTitle } from '@achra/ui/dialog';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@achra/ui/empty';
import { cn } from '@achra/ui/lib/utils';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { FolderOpen, X } from 'lucide-react';
import SimpleBar from 'simplebar-react';
import { useModalCategories } from '../../providers/categories-provider';
import { HeadcountSection } from './headcount-section';
import { NonHeadcountSection } from './non-headcount-section';
function CategoriesModal() {
  const $ = _c(29);
  const {
    headcountCategories,
    nonHeadcountCategories,
    hasNoCategories,
    hasNestedCategories,
    areAllExpanded,
    toggleAllCategories,
    handleHeadcountValueChange,
    handleNonHeadcountValueChange,
    isModalOpen,
    closeModal
  } = useModalCategories();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <DialogOverlay className="z-170!" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn("fixed top-16 left-0 sm:left-[50%]", "sm:translate-x-[-50%]", "z-180", "grid content-start items-start", "h-[calc(100%-64px)] md:h-[calc(100%-128px)]", "max-h-full md:max-h-[813px] lg:max-h-[847px]", "w-full", "max-w-full sm:max-w-[calc(100vw-64px)] md:max-w-[calc(100vw-128px)] lg:max-w-[min(1114px,calc(100vw-128px))] xl:max-w-[min(1184px,calc(100vw-128px))]", "mb-0 gap-0 p-0 sm:mb-12", "no-scrollbar overflow-x-hidden overscroll-contain", "bg-background rounded-none border shadow-lg sm:rounded-lg", "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 duration-200");
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <DialogTitle className="text-xl/[120%] font-bold">Canonical Expense Categories</DialogTitle>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <DialogDescription className="flex-1 pr-8 sm:pr-0">Canonical Expense Categories are standardised classifications of expenses tailored for managing a Team or Networks operational costs.</DialogDescription>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== areAllExpanded || $[5] !== hasNestedCategories || $[6] !== toggleAllCategories) {
    t4 = hasNestedCategories && <div className="flex flex-row-reverse items-center gap-2.5 sm:mr-2.5 sm:flex-row"><label htmlFor="expand-all" className="cursor-pointer text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Expand All Categories</label><Checkbox id="expand-all" checked={areAllExpanded} onCheckedChange={toggleAllCategories} /></div>;
    $[4] = areAllExpanded;
    $[5] = hasNestedCategories;
    $[6] = toggleAllCategories;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = <div className="flex-1">{t2}<div className="mt-2 flex flex-col items-start gap-2 sm:flex-row sm:items-end sm:justify-between sm:gap-4">{t3}{t4}</div></div>;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <X className="h-5 w-5" />;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== closeModal) {
    t7 = <Button variant="ghost" size="icon" onClick={closeModal} aria-label="Close dialog" className="text-popover-foreground hover:text-popover-foreground/80 absolute top-0 right-0">{t6}</Button>;
    $[11] = closeModal;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t5 || $[14] !== t7) {
    t8 = <DialogHeader className="border-input flex flex-col border-b px-4 py-3 text-left"><div className="relative flex w-full flex-row items-start justify-between gap-4">{t5}{t7}</div></DialogHeader>;
    $[13] = t5;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== handleHeadcountValueChange || $[17] !== handleNonHeadcountValueChange || $[18] !== hasNoCategories || $[19] !== headcountCategories || $[20] !== nonHeadcountCategories) {
    t9 = <SimpleBar className="h-full overflow-x-hidden px-4 py-3 sm:px-4 sm:py-3" autoHide={false}><div className="space-y-6 pb-3">{hasNoCategories ? <Empty className="border-border bg-muted/30 w-full border border-solid py-12"><EmptyHeader><EmptyMedia variant="icon"><FolderOpen className="text-muted-foreground size-10" /></EmptyMedia><EmptyTitle>No expense categories</EmptyTitle><EmptyDescription>No expense categories are available for this team.</EmptyDescription></EmptyHeader></Empty> : <><HeadcountSection categories={headcountCategories} onValueChange={handleHeadcountValueChange} /><NonHeadcountSection categories={nonHeadcountCategories} onValueChange={handleNonHeadcountValueChange} /></>}</div></SimpleBar>;
    $[16] = handleHeadcountValueChange;
    $[17] = handleNonHeadcountValueChange;
    $[18] = hasNoCategories;
    $[19] = headcountCategories;
    $[20] = nonHeadcountCategories;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t8 || $[23] !== t9) {
    t10 = <DialogPortal>{t0}<DialogPrimitive.Content className={t1}>{t8}{t9}</DialogPrimitive.Content></DialogPortal>;
    $[22] = t8;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== closeModal || $[26] !== isModalOpen || $[27] !== t10) {
    t11 = <Dialog open={isModalOpen} onOpenChange={closeModal}>{t10}</Dialog>;
    $[25] = closeModal;
    $[26] = isModalOpen;
    $[27] = t10;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  return t11;
}
export { CategoriesModal };